/**
 * Discord's chat formatting, as text.
 *
 * ## What is documented
 *
 * Discord's own "Markdown Text 101" article lists: *italics* (`*x*` or `_x_`), **bold**
 * (`**x**`), underline (`__x__`), ~~strikethrough~~ (`~~x~~`), their combinations
 * (`__**x**__`, `***x***`), headers (`#`, `##`, `###` and a space at the start of a
 * line), subtext (`-# ` at the start of a line), masked links (`[text](url)`), bulleted
 * lists (`-` or `*` and a space, indented by two spaces), inline code and fenced code
 * blocks, block quotes (`> ` for one line, `>>> ` for everything after it) and spoilers
 * (`||x||`, which a code block negates).
 *
 * ANSI colours in a ```ansi code block are not in that article. They are widely used and
 * work in Discord's desktop and web apps; their behaviour on mobile has been
 * inconsistent. The escape sequence is `ESC[<codes>m` with these codes:
 * formats 0 reset, 1 bold, 4 underline; text colours 30–37 (gray, red, green, yellow,
 * blue, pink, cyan, white); background colours 40–47 (dark blue, orange, blue-gray,
 * turquoise, gray, indigo, light gray, white). Discord gives each colour a name of its own
 * that differs from the standard ANSI ones, so this tool names them by what they look like.
 *
 * Nothing here talks to Discord; it only builds the text you paste.
 */

export interface InlineStyles {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  spoiler: boolean;
}

export type BlockKind =
  | "none"
  | "h1"
  | "h2"
  | "h3"
  | "subtext"
  | "bullets"
  | "numbers"
  | "quote"
  | "quoteBlock";

export const NO_STYLES: InlineStyles = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  spoiler: false,
};

const LINE_BREAK = /\r?\n/;
const MARKDOWN_CHARACTERS = /([\\*_~|`])/g;
const ESCAPED_START = /^(\s*)(#{1,3}|-#|>|-|\+|\d+\.)(?= )/;
const TRAILING_BLANK_LINES = /\n+$/;

/** Puts a backslash before every character Discord would read as formatting. */
export function escapeMarkdown(text: string): string {
  return text
    .split(LINE_BREAK)
    .map((line) => {
      const escaped = line.replace(MARKDOWN_CHARACTERS, "\\$1");
      return escaped.replace(
        ESCAPED_START,
        (_whole, space: string, marker: string) =>
          marker.endsWith(".")
            ? `${space}${marker.slice(0, -1)}\\.`
            : `${space}\\${marker}`
      );
    })
    .join("\n");
}

/** Wraps one line of text in the chosen inline markers. Outermost first: spoiler, underline, bold, italic, strike. */
export function wrapInline(text: string, styles: InlineStyles): string {
  const inner = text.trim();
  if (inner === "") {
    return "";
  }
  let open = "";
  let close = "";
  if (styles.spoiler) {
    open += "||";
    close = `||${close}`;
  }
  if (styles.underline) {
    open += "__";
    close = `__${close}`;
  }
  if (styles.bold) {
    open += "**";
    close = `**${close}`;
  }
  if (styles.italic) {
    open += "*";
    close = `*${close}`;
  }
  if (styles.strike) {
    open += "~~";
    close = `~~${close}`;
  }
  return `${open}${inner}${close}`;
}

const BLOCK_PREFIX: Record<
  Exclude<BlockKind, "none" | "numbers" | "quoteBlock">,
  string
> = {
  h1: "# ",
  h2: "## ",
  h3: "### ",
  subtext: "-# ",
  bullets: "- ",
  quote: "> ",
};

/**
 * Formats a block of text: inline styles on each non-empty line, then the block marker.
 * Blank lines stay blank, numbered lists count only the lines that have text, and the
 * multi-line quote puts one `>>> ` before the first line.
 */
export function formatText(
  text: string,
  styles: InlineStyles,
  block: BlockKind,
  escapeText: boolean
): string {
  const source = escapeText ? escapeMarkdown(text) : text;
  let number = 0;
  const lines = source.split(LINE_BREAK).map((line) => {
    const styled = wrapInline(line, styles);
    if (styled === "") {
      return "";
    }
    if (block === "numbers") {
      number += 1;
      return `${number}. ${styled}`;
    }
    if (block === "none" || block === "quoteBlock") {
      return styled;
    }
    return BLOCK_PREFIX[block] + styled;
  });
  const result = lines.join("\n");
  if (block === "quoteBlock" && result.trim() !== "") {
    return `>>> ${result.trimStart()}`;
  }
  return result;
}

/* ------------------------------------------------------------------- links */

export type LinkProblem = "emptyText" | "emptyUrl" | "notHttp";
const HTTP_URL = /^https?:\/\/\S+$/i;

/** `[text](url)`, with parentheses in the address encoded so they cannot end the link. */
export function maskedLink(
  text: string,
  url: string
): { markdown: string } | { problem: LinkProblem } {
  const label = text.trim();
  const address = url.trim();
  if (label === "") {
    return { problem: "emptyText" };
  }
  if (address === "") {
    return { problem: "emptyUrl" };
  }
  if (!HTTP_URL.test(address)) {
    return { problem: "notHttp" };
  }
  const safeLabel = label.replaceAll("[", "\\[").replaceAll("]", "\\]");
  const safeUrl = address.replaceAll("(", "%28").replaceAll(")", "%29");
  return { markdown: `[${safeLabel}](${safeUrl})` };
}

/* -------------------------------------------------------------------- code */

export const CODE_LANGUAGES = [
  "",
  "ansi",
  "js",
  "json",
  "python",
  "css",
  "html",
  "bash",
  "sql",
  "diff",
  "md",
  "java",
  "c",
  "cpp",
  "cs",
  "go",
  "rust",
  "yaml",
] as const;
export type CodeLanguage = (typeof CODE_LANGUAGES)[number];

const FENCE = "```";
const ZERO_WIDTH_SPACE = "​";

/** Inline code. A backtick inside needs a double-backtick span with a space inside each end. */
export function inlineCode(text: string): string {
  if (text === "") {
    return "";
  }
  if (text.includes("`")) {
    return `\`\` ${text} \`\``;
  }
  return `\`${text}\``;
}

/**
 * A fenced code block. Three backticks inside the code would end the block early, so the
 * middle one is moved behind a zero-width space; it looks the same and no longer closes.
 */
export function codeBlock(text: string, language: CodeLanguage): string {
  const body = text
    .replaceAll(FENCE, `\`\`${ZERO_WIDTH_SPACE}\``)
    .replace(TRAILING_BLANK_LINES, "");
  return `${FENCE}${language}\n${body}\n${FENCE}`;
}

/* -------------------------------------------------------------------- ANSI */

export const ESC = "\u001B";

export const ANSI_FOREGROUNDS = [30, 31, 32, 33, 34, 35, 36, 37] as const;
export const ANSI_BACKGROUNDS = [40, 41, 42, 43, 44, 45, 46, 47] as const;

export interface AnsiSegment {
  text: string;
  /** 30–37, or null for the default colour. */
  foreground: number | null;
  /** 40–47, or null for none. */
  background: number | null;
  bold: boolean;
  underline: boolean;
}

/** The `ESC[…m` sequence for a segment's style, or "" when it has none. */
export function ansiCodes(segment: AnsiSegment): string {
  const codes: number[] = [];
  if (segment.bold) {
    codes.push(1);
  }
  if (segment.underline) {
    codes.push(4);
  }
  if (segment.foreground !== null) {
    codes.push(segment.foreground);
  }
  if (segment.background !== null) {
    codes.push(segment.background);
  }
  return codes.length === 0 ? "" : `${ESC}[${codes.join(";")}m`;
}

/**
 * An ```ansi block. Each styled segment is followed by a reset so the next segment starts
 * plain. `visible` writes the escape character as ␛ for showing on screen.
 */
export function ansiBlock(
  segments: readonly AnsiSegment[],
  visible = false
): string {
  const body = segments
    .filter((segment) => segment.text !== "")
    .map((segment) => {
      const open = ansiCodes(segment);
      return open === "" ? segment.text : `${open}${segment.text}${ESC}[0m`;
    })
    .join("");
  const text = `${FENCE}ansi\n${body}\n${FENCE}`;
  return visible ? text.replaceAll(ESC, "␛") : text;
}

/** Discord's message limit for most accounts. */
export const MESSAGE_LIMIT = 2000;
